import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { compose } from 'redux';
import i18n from 'i18next';

import PATH from 'routes/paths';
import TextField from '../../stories/ui/TextField/TextField';
import Button from '../../stories/ui/Button/Button';
import useTextFieldErrorClasses from '../../hooks/styles/useTextFieldErrorClasses';
import InputErrorIcon from '../../components/ui/InputErrorIcon';
import withHelmet from '../../hocs/withHelmet';
import { authRequest } from '../../api/requestWrappers/AuthRequest';
import { LoginLogo } from './Login';

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.gray[100]};
`;

const FormContainer = styled.div`
  width: 340px;
  min-height: 320px;
  background-color: ${({ theme }) => theme.palette.themeCommon.white.main};
  border-radius: 0.5rem;
  padding: 0 1.5rem 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 72px;
  font-size: 1rem;

  &:after {
    content: '';
    position: absolute;
    width: calc(100% + 3rem);
    bottom: 0;
    left: -1.5rem;
    right: -1.5rem;
    border-bottom: 1px solid
      ${({ theme }) => theme.palette.themeCommon.black.light};
  }
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.gray[700]};
  margin: 1.5rem 0;
  line-height: 1.5;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

const StyledSubmitButton = styled(Button)`
  font-size: 1rem;
  width: 100%;
`;

const SuccessMessage = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.gray[1900]};
  margin: 1.5rem 0;
  line-height: 1.5;
`;

const BackLink = styled(Link)`
  display: block;
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function ForgotPassword() {
  const { t } = useTranslation();
  const textFieldErrorClasses = useTextFieldErrorClasses();
  const [submitted, setSubmitted] = useState(false);

  const Schema = Yup.object().shape({
    email: Yup.string().email(t('invalidEmail')).required(t('enterEmail')),
  });

  return (
    <PageContainer>
      <LoginLogo />
      <FormContainer>
        <Header>
          <Trans i18nKey="forgotPassword.title" />
        </Header>

        {submitted ? (
          <>
            <SuccessMessage>
              <Trans i18nKey="forgotPassword.success" />
            </SuccessMessage>
            <BackLink to={PATH.AUTH.LOGIN}>
              <Trans i18nKey="forgotPassword.backToLogin" />
            </BackLink>
          </>
        ) : (
          <>
            <Subtitle>
              <Trans i18nKey="forgotPassword.subtitle" />
            </Subtitle>
            <Formik
              initialValues={{ email: '' }}
              validationSchema={Schema}
              onSubmit={async (values, { setSubmitting }) => {
                // Always land on the success state regardless of API result —
                // never differentiate between known/unknown emails on the FE.
                try {
                  await authRequest.forgotPassword(values.email);
                } catch (e) {
                  // Swallow — same UX whether BE succeeded or hiccupped.
                }
                setSubmitting(false);
                setSubmitted(true);
              }}
            >
              {({ isSubmitting }) => (
                <StyledForm>
                  <Field name="email">
                    {({ field, meta }) => (
                      <StyledTextField
                        type="text"
                        id="forgot-password-email"
                        placeholder={t('emailAddress')}
                        {...field}
                        InputProps={
                          meta.touched && meta.error
                            ? {
                                endAdornment: (
                                  <InputErrorIcon
                                    title={meta.error}
                                    permanentOpen
                                  />
                                ),
                                classes: textFieldErrorClasses,
                              }
                            : undefined
                        }
                      />
                    )}
                  </Field>
                  <StyledSubmitButton
                    color="primary"
                    size="large"
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    <Trans i18nKey="forgotPassword.submit" />
                  </StyledSubmitButton>
                  <BackLink to={PATH.AUTH.LOGIN}>
                    <Trans i18nKey="forgotPassword.backToLogin" />
                  </BackLink>
                </StyledForm>
              )}
            </Formik>
          </>
        )}
      </FormContainer>
    </PageContainer>
  );
}

const wrappers = compose(withHelmet(i18n.t('metaTitles.forgotPassword')));

export default wrappers(ForgotPassword);
